/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { useCallback, useRef } from 'react';
import useStore from '@store/index';
import { TOffsetSortColumn } from '../types';
import {
	TDateFilterOption,
	TSelectOption,
} from '../../../types';
import { cloneDeep } from '@utils/functions';
import dayjs from 'dayjs';
import { DataTableSortStatus } from 'mantine-datatable';

/* -------------------------------------------------------------------------- */
/*                                  Hook                                      */
/* -------------------------------------------------------------------------- */

/**
 * Custom hook for managing offset list functionality.
 * @returns {Array} An array containing references and callback functions.
 */
export const useOffsetList = () => {
	const containerRef = useRef<HTMLDivElement>(null);
	const {
		setOffsetsSortParams,
		setOffsetCreationActiveStep,
		setOffsetOpenDrawer,
		setOffsetAdditionalProjects,
		setOffsetAdditionalProjectsSelected,
		setOffsetMesurabilityProjects,
		setOffsetMesurabilityProjectsSelected,
		setOffsetProjectMixinAco2,
		setOffsetProjectMixinAddi,
		setOffsetStep2Error,
		setOffsetStep3Error,
		setOffsetCreationDatas,
		setOffsetMesurabilityProjectsPage,
		setOffsetAdditionalProjectsPage,
		setOffsetsPage,
		setOffsetsQueryParams,
		offsetsQueryParams,
		setOffsetsSelectedRecords,
		setOffsetsSortStatus,
		setOffsetsPageSize,
	} = useStore();

	/**
	 * Handles sorting parameters.
	 * @param {TOffsetSortColumn} _column - The column to sort by.
	 */
	const handleSortParams = (
		_column: DataTableSortStatus<any>
	) => {
		let sortParams = _column.columnAccessor;
		switch (_column.columnAccessor) {
			case 'brand':
				sortParams = 'entity';
				break;
			case 'name':
				sortParams = 'name';
				break;
			case 'contributionDate':
				sortParams = 'createdAt';
				break;
			case 'quantity':
				sortParams = 'quantity_gco2eq';
				break;
			case 'mesurability':
				sortParams = 'projectMixin_aco2';
				break;
			case 'priceTTC':
				sortParams = 'amount_euro';
				break;
			default:
				// eslint-disable-next-line @typescript-eslint/no-unused-expressions
				sortParams;
				break;
		}

		if (_column.direction == 'asc') {
			setOffsetsSortParams(sortParams);
		} else {
			setOffsetsSortParams('-' + sortParams);
		}
	};

	/**
	 * Closes the drawer and resets related state variables.
	 */
	const handleCloseDrawer = useCallback(() => {
		setOffsetCreationActiveStep(0);
		setOffsetOpenDrawer(false);
		setOffsetAdditionalProjects([]);
		setOffsetAdditionalProjectsSelected([]);
		setOffsetMesurabilityProjects([]);
		setOffsetMesurabilityProjectsSelected([]);
		setOffsetProjectMixinAco2([]);
		setOffsetProjectMixinAddi([]);
		setOffsetStep2Error(false);
		setOffsetStep3Error(false);
		setOffsetCreationDatas({});
		setOffsetMesurabilityProjectsPage(1);
		setOffsetAdditionalProjectsPage(1);
	}, []);

	/**
	 * Filters offset list by selected brands.
	 * @param {TSelectOption[]} brands - Selected brand options.
	 */
	const handleFilterBrands = useCallback(
		(brands: string[]) => {
			setOffsetsSelectedRecords([]);
			setOffsetsPage(1);

			if (brands.length == 0) {
				const { entity, ...rest } = offsetsQueryParams;
				setOffsetsQueryParams(rest);
			} else {
				const queryParamsProjects = {
					entity: brands,
				};
				setOffsetsQueryParams({
					...offsetsQueryParams,
					...queryParamsProjects,
				});
			}
		},
		[]
	);

	/**
	 * Filters offset list by selected date range.
	 * @param {TDateFilterOption[]} date - Selected date range options.
	 */
	const handleFilterDate = useCallback(
		(date: TDateFilterOption[]) => {
			setOffsetsSelectedRecords([]);
			let startDate;
			let endDate;
			if (
				dayjs(date[0].value).isValid() &&
				dayjs(date[1].value).isValid()
			) {
				startDate = date[0].value;
				endDate = date[1].value;
			} else {
				startDate = dayjs()
					.subtract(364, 'days')
					.toISOString();
				endDate = dayjs().toISOString();
			}

			const queryParamsDate = {
				createdAt: {
					$gte: dayjs(startDate).format('YYYY-MM-DD'),
					$lt: dayjs(endDate).format('YYYY-MM-DD'),
				},
			};
			if (
				dayjs(startDate).format('YYYY-MM-DD') ==
				dayjs(endDate).format('YYYY-MM-DD')
			) {
				const cloneOffsetsQueryParams = cloneDeep(
					offsetsQueryParams
				);
				delete cloneOffsetsQueryParams.startDate;
				setOffsetsQueryParams(cloneOffsetsQueryParams);
			} else {
				setOffsetsQueryParams({
					...offsetsQueryParams,
					...queryParamsDate,
				});
			}

			setOffsetsPage(1);
		},
		[]
	);

	/**
	 * Filters offset list by selected producers.
	 * @param {TSelectOption[]} producers - Selected producer options.
	 */
	const handleFilterProducer = useCallback(
		(producers: TSelectOption[]) => {
			setOffsetsSelectedRecords([]);
			setOffsetsPage(1);
			const queryParamsProducers = {
				producer: producers.map(
					(_producer) => _producer.value
				),
			};

			setOffsetsQueryParams({
				...offsetsQueryParams,
				...queryParamsProducers,
			});
		},
		[]
	);

	/**
	 * Filters offset list by selected projects.
	 * @param {TSelectOption[]} projects - Selected project options.
	 */
	const handleFilterProject = useCallback(
		(projects: TSelectOption[]) => {
			setOffsetsSelectedRecords([]);
			setOffsetsPage(1);
			const queryParamsProducers = {
				project: projects.map(
					(_project) => _project.value
				),
			};

			setOffsetsQueryParams({
				...offsetsQueryParams,
				...queryParamsProducers,
			});
		},
		[]
	);

	/**
	 * Handles pagination change.
	 * @param {number} page - The page number to navigate to.
	 */
	const handleChangePage = useCallback(
		(page: number | string) => {
			setOffsetsPage(page);
		},
		[]
	);

	/**
	 * Handles change in sorting status.
	 * @param {TOffsetSortColumn} data - The column and direction to sort by.
	 */
	const handleChangeSortStatus = (
		data: DataTableSortStatus<any>
	) => {
		handleSortParams(data);
		setOffsetsSortStatus(data);
	};

	/**
	 * Handles change in page size.
	 * @param {string | number} _size - The page size to set.
	 */
	const handleChangePageSize = useCallback(
		(_size: string | number) => {
			setOffsetsPageSize(Number(_size));
		},
		[]
	);

	return [
		containerRef,
		handleSortParams,
		handleCloseDrawer,
		handleFilterBrands,
		handleFilterDate,
		handleFilterProducer,
		handleFilterProject,
		handleChangePage,
		handleChangeSortStatus,
		handleChangePageSize,
	] as const;
};
